import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle, XCircle, Clock, RefreshCw, Loader2, Search,
  Building2, User, Mail, Phone, Globe, Download, MapPin, Landmark, FileText, CreditCard,
} from "lucide-react";

interface AffiliateApplication {
  id: string;
  user_id: string;
  applicant_name: string;
  company_name: string | null;
  applicant_type: string;
  email: string;
  phone: string | null;
  experience: string | null;
  reason: string | null;
  commission_preference: string;
  website_url: string | null;
  approval_status: string;
  rejection_reason: string | null;
  created_at: string;
  person_in_charge_name: string | null;
  person_in_charge_title: string | null;
  cr_number: string | null;
  vat_number: string | null;
  national_id_number: string | null;
  date_of_birth: string | null;
  bank_info: { bank_name?: string; iban?: string; account_holder_name?: string } | null;
  national_address: { city?: string; district?: string; street?: string; postal_code?: string; building_number?: string; additional_number?: string } | null;
  document_urls: { company_registration_url?: string; vat_registration_url?: string; national_id_url?: string } | null;
  agreement_pdf_url?: string | null;
  agreement_accepted_at?: string | null;
  agreement_signature_url?: string | null;
  agreement_stamp_url?: string | null;
  country?: string | null;
}

export const AffiliateApprovalPanel = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const isRTL = language === 'ar';
  const t = (en: string, ar: string) => isRTL ? ar : en;

  const [applications, setApplications] = useState<AffiliateApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; application: AffiliateApplication | null }>({ open: false, application: null });
  const [rejectionReason, setRejectionReason] = useState("");

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('affiliate_applications')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setApplications((data || []) as unknown as AffiliateApplication[]);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast({ title: t("Error", "خطأ"), description: t("Failed to fetch applications", "فشل في جلب الطلبات"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApplications(); }, []);

  const handleApprove = async (application: AffiliateApplication) => {
    setUpdating(application.id);
    try {
      const { error: appError } = await supabase
        .from('affiliate_applications')
        .update({ approval_status: 'approved', approved_at: new Date().toISOString(), rejection_reason: null })
        .eq('id', application.id);
      if (appError) throw appError;

      // Best-effort: also flag profiles.user_type for legacy code paths.
      // The dashboard gate now reads affiliate_applications, so this is non-blocking.
      const { data: updatedRows, error: profileError } = await supabase
        .from('profiles')
        .update({ user_type: 'affiliate' as any })
        .eq('id', application.user_id)
        .select('id');

      if (profileError || !updatedRows || updatedRows.length === 0) {
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert(
            { id: application.user_id, username: application.email, user_type: 'affiliate' as any },
            { onConflict: 'id' }
          );

        if (upsertError) {
          console.error('Profile flag write failed (non-fatal, dashboard access still granted):', upsertError);
          toast({
            title: t("Approved with warning", "تمت الموافقة مع تحذير"),
            description: t(
              "Affiliate approved — dashboard access granted. (Profile flag could not be updated.)",
              "تمت الموافقة على الشريك — تم منح الوصول إلى لوحة التحكم. (تعذر تحديث علامة الملف الشخصي.)"
            ),
          });
        }
      }

      setApplications(prev => prev.map(app => app.id === application.id ? { ...app, approval_status: 'approved', rejection_reason: null } : app));
      toast({ title: t("Approved", "تمت الموافقة"), description: `${application.applicant_name} ${t("has been approved as a referral partner", "تمت الموافقة عليه كشريك إحالة")}` });
    } catch (error) {
      console.error('Error approving application:', error);
      toast({ title: t("Error", "خطأ"), description: t("Failed to approve", "فشل في الموافقة"), variant: "destructive" });
    } finally {
      setUpdating(null);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.application) return;
    setUpdating(rejectDialog.application.id);
    try {
      const { error } = await supabase
        .from('affiliate_applications')
        .update({ approval_status: 'rejected', rejection_reason: rejectionReason || null })
        .eq('id', rejectDialog.application.id);
      if (error) throw error;

      setApplications(prev => prev.map(app => app.id === rejectDialog.application!.id ? { ...app, approval_status: 'rejected', rejection_reason: rejectionReason } : app));
      toast({ title: t("Rejected", "تم الرفض"), description: `${rejectDialog.application.applicant_name} ${t("has been rejected", "تم رفضه")}` });
      setRejectDialog({ open: false, application: null });
      setRejectionReason("");
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast({ title: t("Error", "خطأ"), description: t("Failed to reject", "فشل في الرفض"), variant: "destructive" });
    } finally {
      setUpdating(null);
    }
  };

  const handleDownloadDoc = async (path: string, label: string) => {
    try {
      const { data, error } = await supabase.storage.from('affiliate-documents').createSignedUrl(path, 3600);
      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({ title: t("Error", "خطأ"), description: t("Failed to download document", "فشل في تحميل الوثيقة"), variant: "destructive" });
    }
  };

  // Note: legacy applications submitted before browser-side PDF archiving may not have
  // an agreement_pdf_url. The admin can still verify those via the inline signature/stamp
  // thumbnails rendered below.

  const SignedThumb = ({ path, label }: { path: string; label: string }) => {
    const [url, setUrl] = useState<string | null>(null);
    useEffect(() => {
      let cancelled = false;
      (async () => {
        const { data } = await supabase.storage.from('affiliate-documents').createSignedUrl(path, 3600);
        if (!cancelled) setUrl(data?.signedUrl ?? null);
      })();
      return () => { cancelled = true; };
    }, [path]);
    if (!url) return null;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1">
        <div className="h-20 w-32 rounded border border-border bg-background flex items-center justify-center overflow-hidden">
          <img src={url} alt={label} className="max-h-full max-w-full object-contain" />
        </div>
        <span className="text-xs text-muted-foreground">{label}</span>
      </a>
    );
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.applicant_name.toLowerCase().includes(searchQuery.toLowerCase()) || app.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || app.approval_status === activeTab;
    return matchesSearch && matchesTab;
  });

  const counts = {
    pending: applications.filter(a => a.approval_status === 'pending').length,
    approved: applications.filter(a => a.approval_status === 'approved').length,
    rejected: applications.filter(a => a.approval_status === 'rejected').length,
    all: applications.length,
  };

  const InfoRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: string | null | undefined }) => {
    if (!value) return null;
    return (
      <div className="flex items-center gap-2 text-sm">
        <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-muted-foreground">{label}:</span>
        <span className="font-medium">{value}</span>
      </div>
    );
  };

  const ApplicationCard = ({ application }: { application: AffiliateApplication }) => {
    const isExpanded = expandedId === application.id;
    const isAgency = application.applicant_type === 'agency';
    const bank = application.bank_info;
    const addr = application.national_address;
    const docs = application.document_urls;

    return (
      <Card className="overflow-hidden">
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : application.id)}>
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  {isAgency ? <Building2 className="h-5 w-5 text-primary" /> : <User className="h-5 w-5 text-primary" />}
                </div>
                <div>
                  <h3 className="font-semibold">{application.applicant_name}</h3>
                  {application.company_name && <p className="text-sm text-muted-foreground">{application.company_name}</p>}
                </div>
              </div>
              <Badge variant={application.approval_status === 'approved' ? 'default' : application.approval_status === 'pending' ? 'secondary' : 'destructive'}>
                {application.approval_status === 'approved' ? t("Approved", "موافق عليه") : application.approval_status === 'pending' ? t("Pending", "قيد المراجعة") : t("Rejected", "مرفوض")}
              </Badge>
            </div>

            {/* Basic info always visible */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /><span>{application.email}</span></div>
              {application.phone && <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /><span>{application.phone}</span></div>}
              {application.website_url && (
                <div className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" />
                  <a href={application.website_url} target="_blank" rel="noopener noreferrer" className="hover:underline">{t("Website", "الموقع")}</a>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Badge variant="outline" className="text-xs capitalize">
                {isAgency ? t("Agency", "وكالة") : t("Individual", "فرد")}
              </Badge>
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div className="space-y-4 pt-2">
                <Separator />

                {/* Person in Charge */}
                {application.person_in_charge_name && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t("Person in Charge", "المسؤول")}</h4>
                    <InfoRow icon={User} label={t("Name", "الاسم")} value={application.person_in_charge_name} />
                    <InfoRow icon={FileText} label={t("Title", "المسمى")} value={application.person_in_charge_title} />
                  </div>
                )}

                {/* Identity & Registration */}
                <div>
                  <h4 className="text-sm font-semibold mb-2">{t("Identity & Registration", "الهوية والتسجيل")}</h4>
                  {isAgency ? (
                    <>
                      <InfoRow icon={FileText} label={t("CR Number", "السجل التجاري")} value={application.cr_number} />
                      <InfoRow icon={FileText} label={t("VAT Number", "الرقم الضريبي")} value={application.vat_number} />
                    </>
                  ) : (
                    <>
                      <InfoRow icon={FileText} label={application.country === "SA" ? t("National ID or Iqama", "الهوية الوطنية أو الإقامة") : t("National ID", "الهوية الوطنية")} value={application.national_id_number} />
                      <InfoRow icon={FileText} label={t("Date of Birth", "تاريخ الميلاد")} value={application.date_of_birth} />
                    </>
                  )}
                </div>

                {/* Documents */}
                {docs && Object.keys(docs).length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t("Documents", "الوثائق")}</h4>
                    <div className="flex flex-wrap gap-2">
                      {docs.company_registration_url && (
                        <Button size="sm" variant="outline" onClick={() => handleDownloadDoc(docs.company_registration_url!, 'CR')}>
                          <Download className="h-3.5 w-3.5 mr-1" /> {t("Company Registration", "السجل التجاري")}
                        </Button>
                      )}
                      {docs.vat_registration_url && (
                        <Button size="sm" variant="outline" onClick={() => handleDownloadDoc(docs.vat_registration_url!, 'VAT')}>
                          <Download className="h-3.5 w-3.5 mr-1" /> {t("VAT Registration", "التسجيل الضريبي")}
                        </Button>
                      )}
                      {docs.national_id_url && (
                        <Button size="sm" variant="outline" onClick={() => handleDownloadDoc(docs.national_id_url!, 'ID')}>
                          <Download className="h-3.5 w-3.5 mr-1" /> {application.country === "SA" ? t("National ID or Iqama", "الهوية الوطنية أو الإقامة") : t("National ID", "الهوية الوطنية")}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Signed Agreement */}
                {(application.agreement_pdf_url || application.agreement_accepted_at || application.agreement_signature_url || application.agreement_stamp_url) && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t("Signed Agreement", "الاتفاقية الموقعة")}</h4>
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      {application.agreement_pdf_url ? (
                        <Button size="sm" variant="default" onClick={() => handleDownloadDoc(application.agreement_pdf_url!, 'agreement')}>
                          <Download className="h-3.5 w-3.5 mr-1" /> {t("Download PDF", "تحميل PDF")}
                        </Button>
                      ) : (
                        application.agreement_accepted_at && (
                          <span className="text-xs text-muted-foreground italic">
                            {t(
                              "PDF copy not available — applicant signed before automated archiving. Verify via signature & stamp below.",
                              "نسخة PDF غير متوفرة — وقّع المتقدم قبل الأرشفة التلقائية. تحقق عبر التوقيع والختم أدناه."
                            )}
                          </span>
                        )
                      )}
                      {application.agreement_accepted_at && (
                        <span className="text-xs text-muted-foreground">
                          {t("Accepted on", "تم القبول في")} {new Date(application.agreement_accepted_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {(application.agreement_signature_url || application.agreement_stamp_url) && (
                      <div className="flex flex-wrap gap-3 mt-2">
                        {application.agreement_signature_url && (
                          <SignedThumb path={application.agreement_signature_url} label={t("Signature", "التوقيع")} />
                        )}
                        {application.agreement_stamp_url && (
                          <SignedThumb path={application.agreement_stamp_url} label={t("Stamp", "الختم")} />
                        )}
                      </div>
                    )}
                  </div>
                )}
                {bank && (bank.bank_name || bank.iban) && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t("Bank Information", "المعلومات البنكية")}</h4>
                    <InfoRow icon={Landmark} label={t("Bank", "البنك")} value={bank.bank_name} />
                    <InfoRow icon={CreditCard} label={t("IBAN", "آيبان")} value={bank.iban} />
                    <InfoRow icon={User} label={t("Holder", "صاحب الحساب")} value={bank.account_holder_name} />
                  </div>
                )}

                {/* National Address */}
                {addr && (addr.city || addr.street) && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t("National Address", "العنوان الوطني")}</h4>
                    <div className="text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 inline mr-1" />
                      {[addr.building_number, addr.street, addr.district, addr.city, addr.postal_code].filter(Boolean).join(', ')}
                      {addr.additional_number && ` (${t("Add. No.", "رقم إضافي")}: ${addr.additional_number})`}
                    </div>
                  </div>
                )}

                {/* Experience & Reason */}
                {application.experience && (
                  <p className="text-sm text-muted-foreground">
                    <strong>{t("Experience:", "الخبرة:")}</strong> {application.experience}
                  </p>
                )}
                {application.reason && (
                  <p className="text-sm text-muted-foreground">
                    <strong>{t("Reason:", "السبب:")}</strong> {application.reason}
                  </p>
                )}
              </div>
            )}

            {/* Click to expand hint */}
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : application.id)}
            >
              {isExpanded ? t("Show less ▲", "إظهار أقل ▲") : t("Show details ▼", "عرض التفاصيل ▼")}
            </button>

            {application.rejection_reason && application.approval_status === 'rejected' && (
              <p className="text-xs text-destructive">
                {t("Reason:", "سبب الرفض:")} {application.rejection_reason}
              </p>
            )}

            {application.approval_status === 'pending' && (
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleApprove(application)} disabled={updating === application.id}>
                  {updating === application.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-1" />{t("Approve", "موافقة")}</>}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setRejectDialog({ open: true, application })} disabled={updating === application.id}>
                  <XCircle className="h-4 w-4 mr-1" />{t("Reject", "رفض")}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder={t("Search affiliates...", "البحث عن شريك...")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <Button variant="outline" onClick={fetchApplications}>
          <RefreshCw className="h-4 w-4 mr-2" />{t("Refresh", "تحديث")}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending" className="relative">
            <Clock className="h-4 w-4 mr-1" />{t("Pending", "قيد المراجعة")}
            {counts.pending > 0 && <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs">{counts.pending}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved"><CheckCircle className="h-4 w-4 mr-1" />{t("Approved", "موافق عليهم")} ({counts.approved})</TabsTrigger>
          <TabsTrigger value="rejected"><XCircle className="h-4 w-4 mr-1" />{t("Rejected", "مرفوضين")} ({counts.rejected})</TabsTrigger>
          <TabsTrigger value="all">{t("All", "الكل")} ({counts.all})</TabsTrigger>
        </TabsList>
        <TabsContent value={activeTab} className="mt-4">
          {filteredApplications.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">{t("No applications in this category", "لا توجد طلبات في هذه الفئة")}</CardContent></Card>
          ) : (
            <div className="grid gap-4">{filteredApplications.map(app => <ApplicationCard key={app.id} application={app} />)}</div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={rejectDialog.open} onOpenChange={(open) => { setRejectDialog({ open, application: open ? rejectDialog.application : null }); if (!open) setRejectionReason(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Reject Application", "رفض الطلب")}</DialogTitle>
            <DialogDescription>{t(`Are you sure you want to reject ${rejectDialog.application?.applicant_name}?`, `هل أنت متأكد من رفض ${rejectDialog.application?.applicant_name}؟`)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("Rejection Reason (optional)", "سبب الرفض (اختياري)")}</label>
            <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder={t("Enter rejection reason...", "اكتب سبب الرفض...")} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, application: null })}>{t("Cancel", "إلغاء")}</Button>
            <Button variant="destructive" onClick={handleReject} disabled={updating !== null}>
              {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Confirm Rejection", "تأكيد الرفض")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliateApprovalPanel;
